import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';

import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import { useLocation } from 'react-router-dom';
import AuthContext from "flexbiz-core/components/auth/Context";
import { motion } from 'framer-motion';

import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import HomeIcon from '@mui/icons-material/Home';
import MapsHomeWorkOutlinedIcon from '@mui/icons-material/MapsHomeWorkOutlined';
import MapsHomeWorkIcon from '@mui/icons-material/MapsHomeWork';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import PersonIcon from '@mui/icons-material/Person';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import HandshakeIcon from '@mui/icons-material/Handshake';
import { useHistory } from 'react-router-dom';

const NavIcon = ({ selectedIcon, unselectedIcon, isSelected }) => {
    return isSelected ? selectedIcon : unselectedIcon;
};

// Đã bỏ forwardRef vì không cần đo đạc nữa!
const NavButton = ({ label, icon, isSelected, onClick }) => (
    <ButtonBase
        onClick={onClick}
        sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            position: 'relative', // Rất quan trọng để chứa viên thuốc absolute bên trong
            color: isSelected ? 'primary.main' : 'text.secondary',
            transition: 'color 0.3s',
            minWidth: 0, 
            padding: '0 4px', 
        }}
    >
        {/* VIÊN THUỐC NẰM NGAY BÊN TRONG NÚT NÀY */}
        {isSelected && (
            <motion.div
                layoutId="bottom-nav-pill" // Keyword thần thánh để Framer Motion tự làm hiệu ứng bay
                initial={false}
                style={{
                    position: 'absolute',
                    top: '8px',
                    bottom: '8px', // Dùng bottom thay vì height
                    left: 0,
                    right: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    borderRadius: '24px',
                    zIndex: 0, // Nằm lớp dưới
                    boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                }}
                transition={{ type: 'spring', stiffness: 350, damping: 30 }}
            />
        )}

        {/* Bọc Icon và Text lại, set zIndex: 1 để nó nổi lên trên viên thuốc */}
        <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
            {icon}
            <Typography 
                variant="caption" 
                noWrap 
                sx={{ 
                    mt: 0.5, 
                    fontWeight: isSelected ? 'bold' : 'normal',
                    width: '100%', 
                    textAlign: 'center',
                    display: 'block' 
                }}
            >
                {label}
            </Typography>
        </Box>
    </ButtonBase>
);

function ButtonNavi({ defaultValue, code }) {
    const location = useLocation();
    const history = useHistory();
    const context = React.useContext(AuthContext);
    
    const getActiveIndex = useCallback(() => {
        const path = location.pathname;
        if (path.startsWith('/profile')) return 3;
        if (path.startsWith('/broker')) return 2;
        if (path.includes('my-products')) return 1;
        if (path === '/') return 0;
        
        if (code === "DASHBOARD") return 0;
        if (code === "my-products") return 1;
        if (code === "broker") return 2;
        if (code === "profile") return 3;
        
        return defaultValue !== undefined ? defaultValue : 0;
    }, [location.pathname, code, defaultValue]);

    const [value, setValue] = useState(getActiveIndex());

    useEffect(() => {
        setValue(getActiveIndex());
    }, [getActiveIndex]);

    const handleNavigation = (newValue) => {
        setValue(newValue);
        switch(newValue){
            case 0: history.replace('/'); break;
            case 1: history.push('/my-products'); break;
            case 2: history.push(`/broker`); break;
            case 3: 
                history.push({
                    pathname: `/profile`,
                    search: '',
                    state: { previousLocation: location }
                }); 
                break;
            default: break;
        }
    };

    const navItems = [
        { label: context.apis.getLabel("Home") || "Trang chủ", selectedIcon: <HomeIcon />, unselectedIcon: <HomeOutlinedIcon /> },
        { label: context.apis.getLabel("BDS của tôi") || "BĐS của tôi", selectedIcon: <MapsHomeWorkIcon/>, unselectedIcon: <MapsHomeWorkOutlinedIcon /> },
        { label: context.apis.getLabel("Môi giới") || "Môi giới", selectedIcon: <HandshakeIcon />, unselectedIcon: <HandshakeOutlinedIcon /> },
        { label: context.apis.getLabel("Tài khoản") || "Tài khoản", selectedIcon: <PersonIcon />, unselectedIcon: <PersonOutlineOutlinedIcon /> },
    ];

    let pb = (window.ReactNativeWebView ||  window.matchMedia('(display-mode: standalone)').matches)?16:5;
    if(context.queryParams?.os!="ios"){
        pb = `max(${pb}px, env(safe-area-inset-bottom))`
    }else{
        pb = `${pb}px`;
    }
    return (
        <Box sx={{
            position: 'fixed',
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 'calc(100% - 32px)',
            maxWidth: '500px', 
            pb:pb,
            zIndex: 1000 
        }}>
            <Box sx={{
                position: 'relative',
                display: 'flex',
                height: '64px',
                borderRadius: '32px',
                padding: '0 8px', 
                overflow: 'visible', 
                backdropFilter: 'blur(20px)',
                backgroundColor: 'rgba(240, 240, 240, 0.75)',
                border: '1px solid rgba(0, 0, 0, 0.08)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            }}>
                {/* Viên thuốc tổng đã bị xóa đi khỏi đây, nó đã được chuyển vào bên trong NavButton */}
                
                {navItems.map((item, index) => (
                    <NavButton
                        key={index}
                        label={item.label}
                        isSelected={value === index}
                        onClick={() => handleNavigation(index)}
                        icon={<NavIcon
                            isSelected={value === index}
                            selectedIcon={item.selectedIcon}
                            unselectedIcon={item.unselectedIcon}
                        />}
                    />
                ))}
            </Box>
        </Box>
    );
}

ButtonNavi.propTypes = {
    defaultValue: PropTypes.number,
    code: PropTypes.any
};

export default ButtonNavi;